import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import styles from "./NumberStepper.module.scss";

function clamp(n, min, max) {
  if (typeof min === "number" && n < min) return min;
  if (typeof max === "number" && n > max) return max;
  return n;
}

// type="number" reports value="" for invalid input while leaving letters on screen — unrecoverable. Run as text and sanitize.
function sanitizeNumeric(raw, allowNegative) {
  let out = raw.replace(/[^\d.-]/g, "");
  if (allowNegative) {
    out = (out.startsWith("-") ? "-" : "") + out.replace(/-/g, "");
  } else {
    out = out.replace(/-/g, "");
  }
  const firstDot = out.indexOf(".");
  if (firstDot !== -1) {
    out = out.slice(0, firstDot + 1) + out.slice(firstDot + 1).replace(/\./g, "");
  }
  return out;
}

export default function NumberStepper({
  label,
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  disabled = false,
  required = false,
  id,
  className,
  placeholder = "---",
  maxLength = 9,
}) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hasValue = typeof value === "number" && Number.isFinite(value);
  // null prop renders as placeholder "---" so the user can tell "unset" from "0".
  const numericValue = hasValue ? value : 0;
  const displayValue = hasValue ? String(value) : "";

  const focusedRef = useRef(false);
  const [draft, setDraft] = useState(displayValue);
  const [clampHint, setClampHint] = useState(null);

  // Keep the draft in sync with the value prop. While focused we normally
  // hold the draft so in-progress input ("12.", "007", a clamped-while-typing
  // number) survives the re-parsed value echoing back — but still adopt a
  // value the draft does NOT explain, e.g. a save the backend rejected
  // reverting the cached value. Without that the field keeps showing a value
  // the server never accepted until a full reload.
  useEffect(() => {
    setDraft((prev) => {
      if (!focusedRef.current) return displayValue;
      const propNum = hasValue ? value : null;
      const draftEchoesProp =
        prev === ""
          ? propNum === null
          : !Number.isNaN(Number(prev)) &&
            clamp(Number(prev), min, max) === propNum;
      return draftEchoesProp ? prev : displayValue;
    });
  }, [displayValue, value, hasValue, min, max]);

  const commit = useCallback((next) => {
    if (Number.isNaN(next)) return;
    onChange(clamp(next, min, max));
  }, [onChange, min, max]);

  const allowNegative = typeof min !== "number" || min < 0;
  const handleInput = useCallback((e) => {
    const clean = sanitizeNumeric(e.target.value, allowNegative);
    setDraft(clean);
    setClampHint(null);
    // Empty commits null (explicit unset); a quick F5 mid-typing would otherwise lose the edit.
    if (clean === "") {
      commit(null);
      return;
    }
    if (clean === "-" || clean === "." || clean === "-.") return;
    const parsed = Number(clean);
    if (Number.isNaN(parsed)) return;
    if (typeof max === "number" && parsed > max) {
      setClampHint(`${gettext("Max")} ${max}`);
    } else if (typeof min === "number" && parsed < min) {
      setClampHint(`${gettext("Min")} ${min}`);
    }
    commit(clamp(parsed, min, max));
  }, [commit, min, max, allowNegative]);

  const handleFocus = useCallback(() => {
    focusedRef.current = true;
  }, []);

  const handleBlur = useCallback((e) => {
    focusedRef.current = false;
    const raw = e.target.value;
    if (raw === "" || raw === "-") {
      setClampHint(null);
      setDraft(displayValue);
      return;
    }
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) {
      setClampHint(null);
      setDraft(displayValue);
      return;
    }
    // Keep the clamp hint after blur so a quick Tab doesn't hide why the value
    // was capped; it clears on the next edit / in-range blur.
    if (typeof max === "number" && parsed > max) {
      setClampHint(`${gettext("Max")} ${max}`);
    } else if (typeof min === "number" && parsed < min) {
      setClampHint(`${gettext("Min")} ${min}`);
    } else {
      setClampHint(null);
    }
    const clamped = clamp(parsed, min, max);
    setDraft(String(clamped));
    // Only push when blur actually changed the value — re-committing a value
    // the parent already holds would, for a rejected save, just loop another
    // doomed round-trip.
    if (clamped !== (hasValue ? value : null)) commit(clamped);
  }, [commit, min, max, displayValue, hasValue, value]);

  const dec = useCallback(() => {
    const next = clamp(numericValue - step, min, max);
    setDraft(String(next));
    commit(next);
  }, [commit, numericValue, step, min, max]);

  const inc = useCallback(() => {
    const next = clamp(numericValue + step, min, max);
    setDraft(String(next));
    commit(next);
  }, [commit, numericValue, step, min, max]);

  const canDec = !disabled && (typeof min !== "number" || numericValue > min);
  const canInc = !disabled && (typeof max !== "number" || numericValue < max);

  return (
    <div className={`${styles.wrapper} ${className || ""}`}>
      {label && (
        <label className={styles.label} htmlFor={inputId}>
          {label}
          {required && <span className={styles.required}> *</span>}
        </label>
      )}
      <div className={styles.controls}>
        <input
          id={inputId}
          type="text"
          inputMode={Number.isInteger(step) ? "numeric" : "decimal"}
          className={styles.input}
          value={draft}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          onChange={handleInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        <div className={styles.steppers}>
          <button
            type="button"
            className={styles.stepperBtn}
            onClick={inc}
            disabled={!canInc}
            aria-label={gettext("Increase")}
            tabIndex={-1}
          >
            <i className="fa fa-caret-up" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={styles.stepperBtn}
            onClick={dec}
            disabled={!canDec}
            aria-label={gettext("Decrease")}
            tabIndex={-1}
          >
            <i className="fa fa-caret-down" aria-hidden="true" />
          </button>
        </div>
      </div>
      {clampHint && <span className={styles.clampHint}>{clampHint}</span>}
    </div>
  );
}
